import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import Layout from '../components/layout/Layout';
import SEO from '../components/SEO';

const StewardshipPage = () => {
  const areas = [
    { title: 'Animal welfare', desc: 'Supporting secure, appropriate and long-term outcomes for animals requiring care, grazing or rehabilitation.' },
    { title: 'Land stewardship', desc: 'Encouraging responsible, productive and welfare-led use of rural land.' },
    { title: 'Education and skills', desc: 'Supporting practical access to technology, enterprise and early-career development.' },
    { title: 'Community impact', desc: 'Backing focused initiatives where direct action can produce a measurable result.' },
  ];

  return (
    <Layout>
      <SEO
        title="Stewardship | Sandend House"
        description="Learn about Sandend House initiatives spanning animal welfare, responsible land use, education and practical community impact."
        path="/stewardship"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Stewardship",
          "url": "https://theknox.co.uk/stewardship",
          "isPartOf": { "@id": "https://theknox.co.uk/#website" }
        }}
      />

      {/* Hero */}
      <section className="py-20 lg:py-28 border-b border-[var(--sh-rule-light)]">
        <div className="section-container">
          <p className="eyebrow mb-4">Stewardship</p>
          <h1 className="font-serif text-4xl lg:text-5xl mb-8" style={{ maxWidth: '600px' }} data-testid="stewardship-heading">
            Stewardship beyond enterprise.
          </h1>
          <div className="reading-width">
            <p className="text-[var(--sh-charcoal)] text-lg leading-relaxed mb-4">
              Sandend House believes long-term ownership carries responsibilities beyond commercial returns.
            </p>
            <p className="text-[var(--sh-grey)] leading-relaxed">
              Its stewardship work focuses on practical areas where land, capability and sustained involvement can produce meaningful benefit.
            </p>
          </div>
        </div>
      </section>

      {/* Areas of Focus */}
      <section className="py-20 lg:py-28 bg-white border-b border-[var(--sh-rule-light)]">
        <div className="section-container">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 lg:gap-12">
            {areas.map((a, i) => (
              <div key={i} data-testid={`area-${i}`}>
                <h3 className="font-serif text-xl mb-3 text-[var(--sh-ink)]">{a.title}</h3>
                <div className="w-8 h-px bg-[var(--sh-brass)] mb-3"></div>
                <p className="text-[var(--sh-grey)] text-sm leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Grazing Trust */}
      <section className="py-20 lg:py-28 border-b border-[var(--sh-rule-light)]" data-testid="grazing-trust-section">
        <div className="section-container">
          <div className="border border-[var(--sh-rule)] rounded p-8 lg:p-12 max-w-3xl">
            <p className="eyebrow-brass mb-3">Featured Initiative</p>
            <h2 className="font-serif text-3xl mb-4">The Grazing Trust</h2>
            <p className="text-[var(--sh-charcoal)] leading-relaxed mb-4">
              The Grazing Trust is a Scottish not-for-profit initiative focused on animal welfare, secure grazing and responsible land stewardship.
            </p>
            <p className="text-[var(--sh-grey)] leading-relaxed mb-8">
              Its work is being developed to provide practical support, refuge and long-term outcomes for equines and other grazing animals.
            </p>
            <a href="https://thegrazingtrust.com" target="_blank" rel="noopener noreferrer" className="btn-secondary" data-testid="visit-grazing-trust">
              Visit The Grazing Trust <ArrowUpRight size={16} className="ml-2" />
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-20">
        <div className="section-container flex flex-wrap gap-4">
          <Link to="/contact" className="btn-primary">
            Stewardship Enquiries <ArrowRight size={16} className="ml-2" />
          </Link>
          <Link to="/the-house" className="btn-secondary">
            About Sandend House <ArrowRight size={16} className="ml-2" />
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default StewardshipPage;
